import { Injectable, NgZone, inject } from '@angular/core';
import { Router } from '@angular/router';
import { CookieService } from 'ngx-cookie-service';
import { finalize } from 'rxjs';
import { ApiAuthService } from './api/auth/api-auth.service';
import { SiteStateService } from './site-state.service';
import { ClientAccessScopeService } from './client-access-scope.service';
import { MenuPermissionService } from './menu-permission.service';
import { ToastService } from 'angular-toastify';
import {
  ADMIN_LAST_ACTIVITY_KEY,
  ADMIN_SESSION_END_REASON_KEY,
  readAdminLastActivity,
} from '../interceptors/auth-session.interceptor';

/**
 * Auto logout เมื่อไม่มีการใช้งานเกิน idle timeout (ค่าจาก city_parking_auth_setting ผ่าน API)
 * - นับเฉพาะ input จริงของผู้ใช้ (เมาส์/คีย์บอร์ด/สัมผัส/สกรอลล์) ไม่นับ auto-poll ของหน้า
 * - ใช้เวลาล่าสุดร่วมกันทุกแท็บ — แท็บที่เปิดทิ้งไว้จะไม่ดีดผู้ใช้ที่ยังทำงานอยู่อีกแท็บ
 * - ส่ง heartbeat ระหว่างที่ผู้ใช้ยังใช้งาน เพื่อให้ session ฝั่ง API ไม่หมดก่อนหน้าเว็บ
 */
@Injectable({
  providedIn: 'root',
})
export class IdleServiceService {
  private static readonly FALLBACK_IDLE_MINUTES = 50;
  private static readonly CHECK_INTERVAL_MS = 15 * 1000;
  private static readonly ACTIVITY_WRITE_THROTTLE_MS = 5 * 1000;
  private static readonly HEARTBEAT_INTERVAL_MS = 60 * 1000;
  private static readonly WARN_BEFORE_MS = 60 * 1000;

  private idleTimeoutMinutes = IdleServiceService.FALLBACK_IDLE_MINUTES;
  private lastActivityWrite = 0;
  private lastHeartbeatAt = 0;
  private heartbeatInFlight = false;
  private isLoggingOut = false;
  private warned = false;

  private readonly router = inject(Router);
  private readonly ngZone = inject(NgZone);
  private readonly cookieService = inject(CookieService);
  private readonly apiAuth = inject(ApiAuthService);
  private readonly siteState = inject(SiteStateService);
  private readonly clientScope = inject(ClientAccessScopeService);
  private readonly permissionService = inject(MenuPermissionService);
  private readonly toast = inject(ToastService);

  constructor() {
    this.markActivity(true);
    this.ngZone.runOutsideAngular(() => {
      const events = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'wheel', 'scroll'];
      for (const event of events) {
        window.addEventListener(event, () => this.markActivity(), { passive: true, capture: true });
      }
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') this.check();
      });
      setInterval(() => this.check(), IdleServiceService.CHECK_INTERVAL_MS);
    });
  }

  private get hasSession(): boolean {
    return !!this.cookieService.get('token') || !!this.cookieService.get('user_id');
  }

  private get isOnLoginPage(): boolean {
    return this.router.url.startsWith('/login');
  }

  private markActivity(force = false): void {
    const now = Date.now();
    if (!force && now - this.lastActivityWrite < IdleServiceService.ACTIVITY_WRITE_THROTTLE_MS) return;
    this.lastActivityWrite = now;
    this.warned = false;
    try {
      localStorage.setItem(ADMIN_LAST_ACTIVITY_KEY, String(now));
    } catch {
      // ignore
    }
    this.sendHeartbeatIfDue(now);
  }

  private sendHeartbeatIfDue(now: number): void {
    if (this.heartbeatInFlight || !this.hasSession || this.isOnLoginPage) return;
    if (now - this.lastHeartbeatAt < IdleServiceService.HEARTBEAT_INTERVAL_MS) return;

    this.heartbeatInFlight = true;
    this.lastHeartbeatAt = now;
    this.apiAuth
      .sessionHeartbeat()
      .pipe(finalize(() => (this.heartbeatInFlight = false)))
      .subscribe({
        next: (res) => {
          const minutes = Number(res?.idleTimeoutMinutes);
          if (Number.isFinite(minutes) && minutes >= 0) {
            this.idleTimeoutMinutes = minutes;
          }
        },
        error: () => undefined,
      });
  }

  private check(): void {
    if (this.isLoggingOut || this.idleTimeoutMinutes <= 0) return;
    if (!this.hasSession || this.isOnLoginPage) return;

    const idleMs = Date.now() - readAdminLastActivity();
    const timeoutMs = this.idleTimeoutMinutes * 60 * 1000;

    if (idleMs >= timeoutMs) {
      this.ngZone.run(() => this.logoutForIdle());
      return;
    }

    if (!this.warned && idleMs >= timeoutMs - IdleServiceService.WARN_BEFORE_MS) {
      this.warned = true;
      this.ngZone.run(() =>
        this.toast.warn('ไม่มีการใช้งานนานเกินไป — ระบบจะออกจากระบบอัตโนมัติในอีก 1 นาที'),
      );
    }
  }

  private logoutForIdle(): void {
    this.isLoggingOut = true;
    try {
      sessionStorage.setItem(ADMIN_SESSION_END_REASON_KEY, 'session_expired');
    } catch {
      // ignore
    }

    this.apiAuth
      .logout()
      .pipe(finalize(() => this.finishLogout()))
      .subscribe({
        next: () => undefined,
        error: () => undefined,
      });
  }

  private finishLogout(): void {
    this.siteState.clear();
    this.clientScope.clear();
    this.permissionService.resetSession();

    const path = '/';
    const keys = ['token', 'user_id', 'username', 'name', 'company_id', 'must_change_password'];
    for (const key of keys) {
      this.cookieService.delete(key, path);
    }
    this.cookieService.deleteAll(path);

    try {
      localStorage.removeItem('userId');
      localStorage.removeItem('user_id');
    } catch {
      // ignore
    }

    this.lastHeartbeatAt = 0;
    this.markActivity(true);
    this.isLoggingOut = false;
    this.router.navigateByUrl('/login', { replaceUrl: true });
  }
}
